import { useQuery } from '@tanstack/react-query';
import { Building, CircleAlert } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';

import { productVersion } from '../app/brand';
import { Button } from '../components/Button';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { StatusBadge } from '../components/StatusBadge';
import { useAuth } from '../features/auth/AuthProvider';
import {
  getOverview,
  overviewKey,
  statusLabels,
  type Inventory,
  type PlatformOverview,
  type TenantInventory,
} from '../features/platform/api';
import { Occupancy } from '../features/platform/Occupancy';
import { getStorage, storageKey } from '../features/platform/storage';
import {
  listMediaNodes,
  mediaNodesKey,
  statusLabels as nodeStatusLabels,
  spoolFullEvidence,
  uploadFailing,
} from '../features/platform/mediaNodes';
import { useHealth } from '../features/system/HealthProvider';
import { containerView, getServices, servicesKey } from '../features/system/services';
import { SystemDetails } from '../features/system/SystemDetails';
import {
  systemDetailsPath,
  systemSectionId,
  systemStateView,
} from '../features/system/systemState';

const n = (v: number) => v.toLocaleString('pt-BR');
const plural = (v: number, one: string, many: string) => `${n(v)} ${v === 1 ? one : many}`;

/**
 * The platform's home (ADR-028): state of the system and what every tenant
 * holds, so the super administrator sees the whole operation at a glance.
 * The system's details close the page (0.67.0: the former /system screen),
 * reached by the #sistema anchor.
 */
export function PlatformDashboardPage() {
  const { request, can } = useAuth();
  const overview = useQuery({
    queryKey: overviewKey,
    queryFn: () => getOverview(request),
    refetchInterval: 60_000,
  });
  // Shares MediaNodesStrip's query (same key): no second request.
  const canNodes = can('platform.media_nodes');
  const nodes = useQuery({
    queryKey: mediaNodesKey,
    queryFn: () => listMediaNodes(request),
    enabled: canNodes,
    refetchInterval: 15_000,
  });
  useScrollToSystem(!overview.isPending && (!canNodes || !nodes.isPending));

  return (
    <>
      <PageHeader
        title="Painel"
        description="Visão geral da plataforma: empresas, clientes, equipamentos e integrações."
      />
      <div className="space-y-4 p-4 sm:p-6">
        <SystemStrip />
        <DiskRecordingNotice />
        <MediaNodesStrip />
        {overview.isError ? (
          <div className="rounded-lg border border-line bg-surface">
            <EmptyState
              tone="crit"
              icon={CircleAlert}
              title="Não foi possível carregar o painel"
              description="Verifique a conexão com o servidor e tente novamente."
              action={
                <Button variant="primary" onClick={() => void overview.refetch()}>
                  Tentar novamente
                </Button>
              }
            />
          </div>
        ) : (
          <>
            <Totals data={overview.data} />
            <TenantTable data={overview.data} busy={overview.isFetching} />
          </>
        )}
        <SystemDetails />
      </div>
    </>
  );
}

/**
 * Brings the system section into view, with focus for keyboard and screen
 * reader users, whenever the location asks for it (the strip's link, the
 * top bar's badge, an old /system bookmark). Keyed by the navigation so a
 * second click scrolls again, and once more when the content above it
 * (overview, media nodes) first loads and pushes it down.
 */
function useScrollToSystem(settled: boolean) {
  const { hash, key } = useLocation();
  useEffect(() => {
    if (hash !== `#${systemSectionId}`) return;
    const el = document.getElementById(systemSectionId);
    if (!el) return;
    el.scrollIntoView({ block: 'start' });
    if (document.activeElement !== el) el.focus({ preventScroll: true });
  }, [hash, key, settled]);
}

/** Warns while the recordings still go to the server's disk (no bucket yet). */
function DiskRecordingNotice() {
  const { request, can } = useAuth();
  const allowed = can('platform.storage');
  const storage = useQuery({
    queryKey: storageKey,
    queryFn: () => getStorage(request),
    enabled: allowed,
  });
  if (storage.data?.mode !== 'local') return null;
  return (
    <p
      role="status"
      className="flex items-start gap-2 rounded-lg border border-warn/40 bg-warn-soft px-4 py-3 text-[13px] text-fg"
    >
      <CircleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-warn" />
      <span>
        Gravações no disco do servidor:{' '}
        <Link to="/storage" className="font-medium underline underline-offset-2">
          configure o bucket em Armazenamento
        </Link>
      </span>
    </p>
  );
}

/** Readiness, version and the containers, with a way to the details. */
function SystemStrip() {
  const { request, can } = useAuth();
  const { state } = useHealth();
  const view = systemStateView[state];
  const admin = can('system.read');
  const services = useQuery({
    queryKey: servicesKey,
    queryFn: () => getServices(request),
    enabled: admin,
    refetchInterval: 30_000,
  });
  const report = services.data;
  const containers = report?.items.flatMap((s) => (s.container ? [s.container] : [])) ?? [];
  const healthy = containers.filter((c) => containerView(c).tone === 'ok').length;

  return (
    <section
      aria-labelledby="dash-system"
      className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-line bg-surface px-4 py-3"
    >
      <h2 id="dash-system" className="text-[13px] font-medium">
        Resumo do sistema
      </h2>
      <StatusBadge tone={view.tone} pulse={state === 'operational'}>
        {view.label}
      </StatusBadge>
      <p className="text-xs text-fg-muted">
        Versão <span className="font-mono text-fg">{productVersion}</span>
      </p>
      {admin && report?.docker === 'ok' && containers.length > 0 && (
        <p className="text-xs text-fg-muted">
          <span
            className={`font-mono tabular-nums ${healthy < containers.length ? 'text-crit' : 'text-fg'}`}
          >
            {healthy} de {containers.length}
          </span>{' '}
          componentes saudáveis
        </p>
      )}
      {admin && (
        <Link
          to={systemDetailsPath}
          className="ml-auto rounded text-xs text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent"
        >
          Ver detalhes do sistema
        </Link>
      )}
    </section>
  );
}

/**
 * Each media node at a glance (ADR-048/050): availability, occupancy and a
 * pending failover, with a way to the node's page. Only for whoever manages
 * the nodes.
 */
function MediaNodesStrip() {
  const { request, can } = useAuth();
  const allowed = can('platform.media_nodes');
  const nodes = useQuery({
    queryKey: mediaNodesKey,
    queryFn: () => listMediaNodes(request),
    enabled: allowed,
    refetchInterval: 15_000,
  });
  if (!allowed) return null;
  const items = nodes.data?.items.filter((n) => n.status !== 'disabled') ?? [];
  const pending = nodes.data?.failover.pending ?? [];
  const down = items.filter((n) => !n.up).length;

  return (
    <section aria-labelledby="dash-nodes" className="rounded-lg border border-line bg-surface">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-line px-4 py-3">
        <h2 id="dash-nodes" className="text-[13px] font-medium">
          Máquinas de vídeo
        </h2>
        {nodes.data && (
          <p className="text-xs text-fg-muted">
            {down === 0 ? (
              'Todas disponíveis'
            ) : (
              <span className="font-medium text-crit">
                {down === 1 ? '1 indisponível' : `${String(down)} indisponíveis`}
              </span>
            )}
          </p>
        )}
        <Link
          to="/media-nodes"
          className="ml-auto rounded text-xs text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent"
        >
          Ver máquinas de vídeo
        </Link>
      </div>
      {nodes.isError ? (
        <p className="px-4 py-3 text-xs text-crit">
          Não foi possível carregar as máquinas de vídeo.
        </p>
      ) : !nodes.data ? (
        <p className="px-4 py-3 text-xs text-fg-muted">Carregando…</p>
      ) : items.length === 0 ? (
        <p className="px-4 py-3 text-xs text-fg-muted">Nenhuma máquina de vídeo ativa.</p>
      ) : (
        <ul className="divide-y divide-line">
          {pending.map((p) => (
            <li key={`pending-${p.node_id}`} className="px-4 py-2 text-xs text-warn">
              {p.node_name} fora do ar: {p.equipment} equipamento(s) serão movidos
              {p.no_target ? ', mas não há outra máquina disponível' : ''}.
            </li>
          ))}
          {items.map((n) => (
            <li key={n.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5">
              <Link
                to={`/media-nodes/${n.id}`}
                className="min-w-32 rounded text-[13px] font-medium text-fg hover:underline focus-visible:outline-2 focus-visible:outline-accent"
              >
                {n.name}
              </Link>
              <StatusBadge tone={n.up ? 'ok' : 'crit'}>
                {n.up ? 'Disponível' : 'Indisponível'}
              </StatusBadge>
              {n.status === 'paused' && (
                <StatusBadge tone="warn">{nodeStatusLabels[n.status]}</StatusBadge>
              )}
              {uploadFailing(n) && <StatusBadge tone="crit">B2 indisponível</StatusBadge>}
              {spoolFullEvidence(n) && (
                <StatusBadge tone="crit">Disco cheio de evidência</StatusBadge>
              )}
              <div className="ml-auto">
                <Occupancy node={n} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Stat({
  label,
  value,
  detail,
  alert = false,
}: {
  label: string;
  value: number | undefined;
  detail?: ReactNode;
  alert?: boolean;
}) {
  return (
    <div className="rounded-lg border border-line bg-surface px-4 py-3">
      <dt className="text-2xs tracking-wide text-fg-subtle uppercase">{label}</dt>
      <dd className="mt-1">
        {value === undefined ? (
          <div aria-hidden="true" className="h-7 w-12 animate-pulse rounded bg-surface-hover" />
        ) : (
          <span
            className={`font-mono text-2xl leading-7 font-semibold tabular-nums ${alert ? 'text-crit' : 'text-fg'}`}
          >
            {n(value)}
          </span>
        )}
      </dd>
      {detail !== undefined && value !== undefined && (
        <dd className="mt-0.5 truncate text-xs text-fg-muted">{detail}</dd>
      )}
    </div>
  );
}

function Totals({ data }: { data: PlatformOverview | undefined }) {
  const t = data?.totals;
  const down = t ? t.cameras_down + t.recorders_down : undefined;
  return (
    <section aria-labelledby="dash-totals">
      <h2 id="dash-totals" className="sr-only">
        Totais da plataforma
      </h2>
      <dl className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat
          label="Empresas"
          value={t?.tenants}
          detail={
            t &&
            (t.tenants_suspended
              ? plural(t.tenants_suspended, 'suspensa', 'suspensas')
              : 'todas ativas')
          }
        />
        <Stat label="Clientes" value={t?.clients} />
        <Stat label="Contas" value={t?.accounts} />
        <Stat label="Usuários" value={t?.users} />
        <Stat
          label="Câmeras"
          value={t && t.ip_cameras + t.analog_cameras}
          detail={t && `${n(t.ip_cameras)} IP · ${n(t.analog_cameras)} analógicas`}
        />
        <Stat
          label="DVRs e NVRs"
          value={t && t.dvrs + t.nvrs}
          detail={t && `${n(t.dvrs)} DVR · ${n(t.nvrs)} NVR`}
        />
        <Stat
          label="Sem comunicação"
          value={down}
          alert={Boolean(down)}
          detail={
            t &&
            `${plural(t.cameras_down, 'câmera', 'câmeras')} · ${plural(t.recorders_down, 'gravador', 'gravadores')}`
          }
        />
        <Stat label="Integrações ativas" value={t?.integrations} />
      </dl>
    </section>
  );
}

const columns: { key: keyof Inventory; label: string; title?: string }[] = [
  { key: 'clients', label: 'Clientes' },
  { key: 'accounts', label: 'Contas' },
  { key: 'users', label: 'Usuários' },
  { key: 'ip_cameras', label: 'Câm. IP', title: 'Câmeras IP: avulsas e canais de NVR' },
  {
    key: 'analog_cameras',
    label: 'Câm. analóg.',
    title: 'Câmeras analógicas: canais de DVR e encoders',
  },
  { key: 'dvrs', label: 'DVR' },
  { key: 'nvrs', label: 'NVR' },
  { key: 'integrations', label: 'Integr.', title: 'Integrações ativas' },
];

const downOf = (i: Inventory) => i.cameras_down + i.recorders_down;

function TenantTable({ data, busy }: { data: PlatformOverview | undefined; busy: boolean }) {
  return (
    <section
      aria-labelledby="dash-tenants"
      className="overflow-hidden rounded-lg border border-line bg-surface"
    >
      <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-2.5">
        <h2 id="dash-tenants" className="text-[13px] font-medium">
          Por empresa
        </h2>
        <Link
          to="/tenants"
          className="rounded text-xs text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent"
        >
          Gerenciar empresas
        </Link>
      </div>
      {data && data.items.length === 0 ? (
        <EmptyState
          icon={Building}
          title="Nenhuma empresa ainda"
          description="Crie a primeira empresa em Empresas."
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-[13px]" aria-busy={busy}>
            <caption className="sr-only">O que cada empresa tem na plataforma</caption>
            <thead className="text-2xs tracking-wide text-fg-subtle uppercase">
              <tr className="border-b border-line">
                <th scope="col" className="px-4 py-2 font-medium">
                  Empresa
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  Situação
                </th>
                {columns.map((c) => (
                  <th
                    key={c.key}
                    scope="col"
                    title={c.title}
                    className="px-3 py-2 text-right font-medium"
                  >
                    {c.label}
                  </th>
                ))}
                <th
                  scope="col"
                  title="Câmeras e gravadores sem comunicação"
                  className="px-4 py-2 text-right font-medium"
                >
                  Sem com.
                </th>
              </tr>
            </thead>
            <tbody>
              {!data
                ? Array.from({ length: 3 }, (_, i) => (
                    <tr key={i} aria-hidden="true" className="border-b border-line last:border-0">
                      <td className="px-4 py-3.5" colSpan={columns.length + 3}>
                        <div className="h-3 w-2/3 animate-pulse rounded bg-surface-hover" />
                      </td>
                    </tr>
                  ))
                : data.items.map((t) => <TenantRow key={t.id} t={t} />)}
            </tbody>
            {data && data.items.length > 1 && (
              <tfoot className="border-t border-line-strong text-xs font-medium text-fg">
                <tr>
                  <th scope="row" colSpan={2} className="px-4 py-2.5 text-left font-medium">
                    Total
                  </th>
                  {columns.map((c) => (
                    <td key={c.key} className="px-3 py-2.5 text-right font-mono tabular-nums">
                      {n(data.totals[c.key])}
                    </td>
                  ))}
                  <td
                    className={`px-4 py-2.5 text-right font-mono tabular-nums ${downOf(data.totals) ? 'text-crit' : ''}`}
                  >
                    {n(downOf(data.totals))}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </section>
  );
}

function TenantRow({ t }: { t: TenantInventory }) {
  const down = downOf(t);
  return (
    <tr className="border-b border-line last:border-0 hover:bg-surface-hover/50">
      <th scope="row" className="px-4 py-2.5 text-left font-normal">
        <div className="font-medium text-fg">{t.name}</div>
        <div className="font-mono text-xs text-fg-subtle">{t.slug}</div>
      </th>
      <td className="px-4 py-2.5">
        <StatusBadge tone={t.status === 'active' ? 'ok' : 'warn'}>
          {statusLabels[t.status]}
        </StatusBadge>
      </td>
      {columns.map((c) => (
        <td
          key={c.key}
          className="px-3 py-2.5 text-right font-mono text-xs text-fg-muted tabular-nums"
        >
          {n(t[c.key])}
        </td>
      ))}
      <td
        className={`px-4 py-2.5 text-right font-mono text-xs tabular-nums ${down ? 'font-semibold text-crit' : 'text-fg-muted'}`}
        title={
          down
            ? `${plural(t.cameras_down, 'câmera', 'câmeras')} e ${plural(t.recorders_down, 'gravador', 'gravadores')} sem comunicação`
            : undefined
        }
      >
        {n(down)}
      </td>
    </tr>
  );
}
